import React from "react";
import * as yup from "yup";
import { useTheme } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";
import useUserProfile from "@app.automotus.io/components/hooks/useUserProfile";
import Grid from "@mui/material/Grid";
import TextField from "@mui/material/TextField";
import MenuItem from "@mui/material/MenuItem";
import { useFormikContext } from "formik";
import { CompanyValue } from "@app.automotus.io/components/common";
import { CurbPassAccountOwnerType, CURB_PASS_OWNER_TYPE_OPTIONS } from "common/graphql/fragments";
import FirstNameField from "@app.automotus.io/components/forms/ProfileInformationFields/FirstNameField";
import LastNameField from "@app.automotus.io/components/forms/ProfileInformationFields/LastNameField";
import EmailField from "@app.automotus.io/components/forms/ProfileInformationFields/EmailField";
import PhoneNumberField from "@app.automotus.io/components/forms/ProfileInformationFields/PhoneNumberField";
import CompanyAutocompleteField from "@app.automotus.io/components/forms/ProfileInformationFields/CompanyAutocompleteField";

const INPUT_SIZE_MOBILE = 12;
const INPUT_SIZE_NON_MOBILE = 12;

export type CurbPassOwnerType = CurbPassAccountOwnerType;

/**
 * Component that renders a set of fields that a user can use to populate his profile information.
 * Must be used inside of a Formik context.
 *
 * This component is designed to be reusable and can be composed with other form fields.
 */
export const ProfileInformationFields: React.FC & ProfileInformationFieldsExtension = () => {
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up("lg"));
  const isSmall = useMediaQuery(theme.breakpoints.down("tiny"));
  const { userProfile } = useUserProfile();
  const { values, setFieldValue, touched, errors } = useFormikContext<ProfileInformationValues>();
  const isPhoneNumberDisabled = !!userProfile?.identities[0].phoneNumber;

  return (
    <Grid
      container
      direction="row"
      alignItems="space-between"
      rowSpacing={isDesktop ? 4.5 : isSmall ? 3.5 : 4}
      columnSpacing={2}
    >
      <Grid item xs={INPUT_SIZE_MOBILE} md={INPUT_SIZE_NON_MOBILE}>
        <FirstNameField />
      </Grid>
      <Grid item xs={INPUT_SIZE_MOBILE} md={INPUT_SIZE_NON_MOBILE}>
        <LastNameField />
      </Grid>
      <Grid item xs={INPUT_SIZE_MOBILE} md={INPUT_SIZE_NON_MOBILE}>
        <EmailField />
      </Grid>
      <Grid item xs={INPUT_SIZE_MOBILE} md={INPUT_SIZE_NON_MOBILE}>
        <PhoneNumberField disabled={isPhoneNumberDisabled} />
      </Grid>
      <Grid item xs={INPUT_SIZE_MOBILE} md={INPUT_SIZE_NON_MOBILE}>
        <TextField
          select
          fullWidth
          label="Account Type"
          name="ownerType"
          value={values.ownerType}
          onChange={(e) => {
            const value = e.target.value as CurbPassOwnerType;
            setFieldValue("ownerType", value);
            if (value === "individual_driver") {
              setFieldValue("organizationName", "");
            }
          }}
        >
          {CURB_PASS_OWNER_TYPE_OPTIONS.map(({ value, label }) => (
            <MenuItem key={value} value={value}>
              {label}
            </MenuItem>
          ))}
        </TextField>
      </Grid>
      {values.ownerType === "fleet_operator" && (
        <Grid item xs={INPUT_SIZE_MOBILE} md={INPUT_SIZE_NON_MOBILE}>
          <TextField
            fullWidth
            label="Organization Name"
            name="organizationName"
            value={values.organizationName}
            onChange={(e) => setFieldValue("organizationName", e.target.value)}
            error={touched.organizationName && !!errors.organizationName}
            helperText={touched.organizationName && errors.organizationName}
            required
          />
        </Grid>
      )}
    </Grid>
  );
};

const validationSchema = yup.object({
  firstName: FirstNameField.validationSchema,
  lastName: LastNameField.validationSchema,
  email: EmailField.validationSchema,
  phoneNumber: PhoneNumberField.validationSchema,
  companies: CompanyAutocompleteField.validationSchema,
  ownerType: yup.string().oneOf(["individual_driver", "fleet_operator"]).required(),
  organizationName: yup.string().when("ownerType", {
    is: "fleet_operator",
    then: (schema) => schema.required("Organization name is required"),
    otherwise: (schema) => schema.notRequired(),
  }),
});

ProfileInformationFields.validationSchema = validationSchema;

ProfileInformationFields.defaultInitialValues = {
  firstName: FirstNameField.defaultInitialValue,
  lastName: LastNameField.defaultInitialValue,
  email: EmailField.defaultInitialValue,
  phoneNumber: PhoneNumberField.defaultInitialValue,
  companies: CompanyAutocompleteField.defaultInitialValue,
  ownerType: "individual_driver",
  organizationName: "",
};

/** Values of all fields in contained in the {@link ProfileInformationFields} field set */
export interface ProfileInformationValues {
  /** First name of the user */
  firstName: string;
  /** Last name of the user */
  lastName: string;
  /** Email address */
  email: string;
  /** Phone number */
  phoneNumber: string;
  /** Company selection */
  companies: CompanyValue[] | null;
  /** Account owner type */
  ownerType: CurbPassOwnerType;
  /** Organization name (required for fleet operators) */
  organizationName: string;
}

export interface ProfileInformationFieldsExtension {
  /** Validation schema that can be used to validate the field values */
  validationSchema: ReturnType<(typeof yup)["object"]>;
  /** Default initial values for the fields */
  defaultInitialValues: ProfileInformationValues;
}

export default ProfileInformationFields;
